import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, EyeIcon, EyeOffIcon, LoaderIcon, LockIcon, MailIcon } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x8qyon7Q7FP4Ysx38EhDqB/7b33b6e2-5996-44ed-b635-9846f7d8d920.png';
const BRAND_IMAGE_URL = 'https://a.storyblok.com/f/312547/6000x4000/39a62afe76/derby-25.jpg/m/1600x0';
const inputCls = 'w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-[#14466B] focus:outline-none focus:ring-2 focus:ring-[#14466B]/15';
type Mode = 'signIn' | 'reset';
export function SignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [status, setStatus] = useState<'idle' | 'submitting'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const handleSignIn = (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your email address and password to continue.');
      return;
    }
    setError(null);
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('idle');
      navigate('/jobs');
    }, 900);
  };
  const handleReset = (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      setError('Enter the email address linked to your account.');
      return;
    }
    setError(null);
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('idle');
      setResetSentTo(email.trim());
    }, 900);
  };
  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setResetSentTo(null);
  };
  return <div className="min-h-screen w-full bg-white lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]" data-id="element-2054" data-mp="1az0hfi875sg0">
      {/* Left — credentials */}
      <main className="flex min-h-screen flex-col px-6 py-10 sm:px-12 lg:min-h-0 lg:px-16 xl:px-24" data-id="element-2055" data-mp="1255or9o9huh9">
        <img src={LOGO_URL} alt="GCA" className="h-12 w-auto self-start object-contain lg:hidden" data-id="element-2056" data-mp="x6koja90fast" />

        <div className="flex flex-1 flex-col justify-center py-10 lg:py-0" data-id="element-2057" data-mp="1tva6f0kn03es">
          <div className="w-full max-w-[400px]" data-id="element-2058" data-mp="1puq7pskf0rqj">
            {mode === 'signIn' ? <>
                <h1 className="text-[30px] font-semibold leading-tight tracking-tight text-slate-900" data-id="element-2059" data-mp="1kv3sjdl111f3">
                  Sign in
                </h1>

                <form onSubmit={handleSignIn} className="mt-8 space-y-4" noValidate data-id="element-2060" data-mp="1fz6e5ctbq54y">
                  <div data-id="element-2061" data-mp="9i1bkhf0m9ue">
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-2062" data-mp="4hn6974ste02">
                      Work email
                    </label>
                    <div className="relative" data-id="element-2063" data-mp="3vhe81h81ckt3">
                      <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-2064" data-mp="3vg349helbd1z" />
                      <input id="email" type="email" autoComplete="email" value={email} onChange={event => {
                    setEmail(event.target.value);
                    setError(null);
                  }} className={inputCls} placeholder="you@gca-consulting.com" data-id="element-2065" data-mp="d47xjp2leyqw" />
                    </div>
                  </div>

                  <div data-id="element-2066" data-mp="2ts23ocj1d4vs">
                    <div className="mb-1.5 flex items-baseline justify-between gap-3" data-id="element-2067" data-mp="2ttdeuxet9ewx">
                      <label htmlFor="password" className="block text-sm font-medium text-slate-700" data-id="element-2068" data-mp="o38dgctwkkfz">
                        Password
                      </label>
                      <button type="button" onClick={() => switchMode('reset')} className="text-xs font-medium text-[#14466B] transition-colors hover:text-[#0F3D5C] hover:underline focus:outline-none focus-visible:underline" data-id="element-2069" data-mp="x2vgt46700bo">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative" data-id="element-2070" data-mp="9fn96flakf6h">
                      <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-2071" data-mp="9evi9i2n35mj" />
                      <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={event => {
                    setPassword(event.target.value);
                    setError(null);
                  }} className={`${inputCls} pr-11`} placeholder="••••••••" data-id="element-2072" data-mp="3cw3xrv9w9te3" />
                      <button type="button" onClick={() => setShowPassword(value => !value)} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#14466B]/20" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2073" data-mp="2g3xi8a1l833e">
                        {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-2074" data-mp="bm7mn74yy48w" /> : <EyeIcon className="h-4 w-4" data-id="element-2075" data-mp="7jujmqhlwkft" />}
                      </button>
                    </div>
                  </div>

                  <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm text-slate-600" data-id="element-2076" data-mp="2rjgzfvhekjk">
                    <input type="checkbox" checked={remember} onChange={event => setRemember(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#14466B] focus:ring-[#14466B]/30" data-id="element-2077" data-mp="3ty4ffknv8uqm" />
                    Keep me signed in on this device
                  </label>

                  {error && <p role="alert" className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2.5 text-sm text-rose-700" data-id="element-2078" data-mp="1e8jd6mky9jiv">
                      <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-2079" data-mp="1gtilxpoyppex" />
                      {error}
                    </p>}

                  <button type="submit" disabled={status === 'submitting'} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#6A9A24] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#587F1D] focus:outline-none focus:ring-2 focus:ring-[#6A9A24]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-2080" data-mp="1lqhguagga0e6">
                    {status === 'submitting' ? <>
                        <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-2081" data-mp="17ux6w4ip8siw" />
                        Signing in…
                      </> : <>
                        Sign in
                        <ArrowRightIcon className="h-4 w-4" data-id="element-2082" data-mp="2n91dqqwqmjsw" />
                      </>}
                  </button>
                </form>

                <p className="mt-8 text-sm text-slate-500" data-id="element-2083" data-mp="39bd4yappp6oa">
                  Need an account?{' '}
                  <a href="mailto:derby@gca-consulting.com" className="font-medium text-[#14466B] hover:underline" data-id="element-2084" data-mp="34dlb3b5ibahg">
                    Ask your administrator
                  </a>
                </p>
              </> : <>
                <button type="button" onClick={() => switchMode('signIn')} className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-[#14466B] focus:outline-none focus-visible:underline" data-id="element-2085" data-mp="3s3fllm2csmyj">
                  <ArrowLeftIcon className="h-4 w-4" data-id="element-2086" data-mp="27lhq2ade64mz" />
                  Back to sign in
                </button>

                {resetSentTo ? <div data-id="element-2087" data-mp="22npzbjohgokd">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600" data-id="element-2088" data-mp="22o95620uzmi1">
                      <CheckCircle2Icon className="h-5 w-5" data-id="element-2089" data-mp="n3782l3mukmv" />
                    </span>
                    <h1 className="mt-4 text-[26px] font-semibold leading-tight tracking-tight text-slate-900" data-id="element-2090" data-mp="8ekhfn8k26gc">
                      Check your inbox
                    </h1>
                    <p className="mt-2 text-sm text-slate-500" data-id="element-2091" data-mp="dbt4a14neynx">
                      We've sent password reset instructions to{' '}
                      <span className="font-medium text-slate-800" data-id="element-2092" data-mp="3f7wj818v9ge">
                        {resetSentTo}
                      </span>
                      . The link expires in 30 minutes.
                    </p>
                    <button type="button" onClick={() => setResetSentTo(null)} className="mt-6 text-sm font-medium text-[#14466B] transition-colors hover:text-[#0F3D5C] hover:underline" data-id="element-2093" data-mp="3qjnqtk5k14mk">
                      Use a different email address
                    </button>
                  </div> : <>
                    <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-slate-900" data-id="element-2094" data-mp="2seebzf692duv">
                      Reset your password
                    </h1>
                    <p className="mt-2 text-sm text-slate-500" data-id="element-2095" data-mp="2jkrjpz193sqf">
                      Enter your work email and we'll send you a secure link to
                      set a new password.
                    </p>

                    <form onSubmit={handleReset} className="mt-8 space-y-4" noValidate data-id="element-2096" data-mp="3bcqisu9ovbna">
                      <div data-id="element-2097" data-mp="2xd7hakbduyjy">
                        <label htmlFor="reset-email" className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-2098" data-mp="2xbwdwllunstd">
                          Work email
                        </label>
                        <div className="relative" data-id="element-2099" data-mp="1qwc3582h12cz">
                          <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-2100" data-mp="1qxx014c9cxe0" />
                          <input id="reset-email" type="email" autoComplete="email" value={email} onChange={event => {
                      setEmail(event.target.value);
                      setError(null);
                    }} className={inputCls} placeholder="you@gca-consulting.com" data-id="element-2101" data-mp="1sdsrwp6p764t" />
                        </div>
                      </div>

                      {error && <p role="alert" className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2.5 text-sm text-rose-700" data-id="element-2102" data-mp="gxkic3sumujn">
                          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-2103" data-mp="2c0zc8x97bmm6" />
                          {error}
                        </p>}

                      <button type="submit" disabled={status === 'submitting'} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#6A9A24] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#587F1D] focus:outline-none focus:ring-2 focus:ring-[#6A9A24]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-2104" data-mp="2lr24uwceuyjz">
                        {status === 'submitting' ? <>
                            <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-2105" data-mp="2740kumgx9tro" />
                            Sending link…
                          </> : 'Send reset link'}
                      </button>
                    </form>
                  </>}
              </>}
          </div>
        </div>

        <p className="text-xs text-slate-400" data-id="element-2106" data-mp="12tpceryuuw4n">
          © {new Date().getFullYear()} GCA (UK) Ltd. All rights reserved.
        </p>
      </main>

      {/* Right — branding */}
      <aside className="relative hidden overflow-hidden bg-[#1F5C9E] lg:block" data-id="element-2107" data-mp="t2b723k8323l">
        <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{
        backgroundImage: `url("${BRAND_IMAGE_URL}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} aria-hidden="true" data-id="element-2108" data-mp="o49nzimsr2s4" />
        <div className="absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full border border-white/10" aria-hidden="true" data-id="element-2109" data-mp="a4ru4oun8zbw" />
        <div className="absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full border border-white/10" aria-hidden="true" data-id="element-2110" data-mp="29f9sf02ufn4d" />

        <div className="relative z-10 flex h-full flex-col items-center justify-center p-14 text-center xl:p-16" data-id="element-2111" data-mp="118ezmra168u9">
          <img src={LOGO_URL} alt="GCA" className="h-28 w-auto object-contain" data-id="element-2112" data-mp="1655gvhxknah9" />
          <p className="mt-2 text-sm font-medium uppercase tracking-[0.18em] text-[#8CC63F]" data-id="element-2113" data-mp="s8rv3wm08mwz">
            Civil &amp; Structural Engineering Services
          </p>
        </div>
      </aside>
    </div>;
}